// ============================================================================
// stockSupplierService — Supplier Directory Layer
// ----------------------------------------------------------------------------
// Manages external stock suppliers scoped by workspaceId.
// ============================================================================

import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type DocumentData,
  type Firestore,
  type Timestamp,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { isNoSignInPreviewActive } from '../debug'
import type { StockSupplier } from '../types/stock'
import type { FieldSection } from '../components/Common/EntityFormDialog'

export const STOCK_SUPPLIERS_COLLECTION = 'stock_suppliers'

export const SUPPLIER_FORM_SECTIONS: FieldSection[] = [
  {
    id: 'basics',
    label: 'Basics',
    fields: [
      { id: 'name', label: 'Supplier name', type: 'text', required: true, placeholder: 'e.g. Acme Supplies' },
      { id: 'contactEmail', label: 'Contact email', type: 'text', placeholder: 'supplier@example.com' },
      { id: 'contactPhone', label: 'Contact phone', type: 'text', placeholder: '+1 (555) 000-0000' },
    ],
  },
  {
    id: 'details',
    label: 'Details',
    fields: [
      { id: 'address', label: 'Address', type: 'textarea', placeholder: '123 Main St, Suite 100...' },
      { id: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Lead times, account manager notes...' },
    ],
  },
]

const toStockSupplier = (id: string, data: DocumentData): StockSupplier => ({
  id,
  workspaceId: String(data.workspaceId ?? ''),
  name: String(data.name ?? ''),
  contactEmail: data.contactEmail != null ? String(data.contactEmail) : null,
  contactPhone: data.contactPhone != null ? String(data.contactPhone) : null,
  address: data.address != null ? String(data.address) : null,
  notes: data.notes != null ? String(data.notes) : null,
  createdAt: (data.createdAt as Timestamp | null) ?? null,
})

export interface CreateStockSupplierInput {
  workspaceId: string
  name: string
  contactEmail?: string | null
  contactPhone?: string | null
  address?: string | null
  notes?: string | null
}

export const subscribeStockSuppliers = (
  workspaceId: string,
  onData: (suppliers: StockSupplier[]) => void,
  onError?: (error: Error) => void,
  db: Firestore = firestoreDb,
) => {
  if (!workspaceId) {
    onData([])
    return () => {}
  }
  if (isNoSignInPreviewActive()) {
    onData([])
    return () => {}
  }

  const q = query(
    collection(db, STOCK_SUPPLIERS_COLLECTION),
    where('workspaceId', '==', workspaceId),
  )

  return onSnapshot(
    q,
    (snapshot) => {
      const items = snapshot.docs.map((docSnap) =>
        toStockSupplier(docSnap.id, docSnap.data()),
      )
      onData(items.sort((a, b) => a.name.localeCompare(b.name)))
    },
    (error) => {
      console.warn('[stockSupplierService] subscription error', { workspaceId }, error)
      onError?.(error)
    },
  )
}

export const createStockSupplier = async (
  input: CreateStockSupplierInput,
  db: Firestore = firestoreDb,
): Promise<string> => {
  if (!input.workspaceId) throw new Error('workspaceId is required to create a supplier')
  if (!input.name.trim()) throw new Error('Supplier name is required')

  const ref = doc(collection(db, STOCK_SUPPLIERS_COLLECTION))
  await setDoc(ref, {
    workspaceId: input.workspaceId,
    name: input.name.trim(),
    contactEmail: input.contactEmail?.trim() || null,
    contactPhone: input.contactPhone?.trim() || null,
    address: input.address?.trim() || null,
    notes: input.notes?.trim() || null,
    createdAt: serverTimestamp(),
  })
  return ref.id
}

export const updateStockSupplier = async (
  id: string,
  patch: Partial<Omit<StockSupplier, 'id' | 'workspaceId' | 'createdAt'>>,
  db: Firestore = firestoreDb,
): Promise<void> => {
  if (!id) throw new Error('Supplier id is required for update')
  const ref = doc(db, STOCK_SUPPLIERS_COLLECTION, id)
  const updatePayload: Record<string, unknown> = {}

  if (patch.name !== undefined) updatePayload.name = patch.name.trim()
  if (patch.contactEmail !== undefined) updatePayload.contactEmail = patch.contactEmail?.trim() || null
  if (patch.contactPhone !== undefined) updatePayload.contactPhone = patch.contactPhone?.trim() || null
  if (patch.address !== undefined) updatePayload.address = patch.address?.trim() || null
  if (patch.notes !== undefined) updatePayload.notes = patch.notes?.trim() || null

  await updateDoc(ref, updatePayload)
}

export const deleteStockSupplier = async (
  id: string,
  db: Firestore = firestoreDb,
): Promise<void> => {
  if (!id) throw new Error('Supplier id is required for delete')
  const ref = doc(db, STOCK_SUPPLIERS_COLLECTION, id)
  await deleteDoc(ref)
}
